// Routes, sign-in and the states every screen shares: loading, load errors, a subscription that
// has ended, and an account with nothing uploaded yet.

import { useEffect } from "react";
import { Shell, type Section } from "./components/Shell.tsx";
import { ToastProvider } from "./components/Toast.tsx";
import { AccountProvider, useAccount } from "./data/AccountData.tsx";
import { type Route, navigate, useRoute } from "./lib/router.ts";
import { signOut, useSession } from "./lib/session.ts";
import { Account } from "./screens/Account.tsx";
import { DevSession } from "./screens/DevSession.tsx";
import { Empty } from "./screens/Empty.tsx";
import { Export } from "./screens/Export.tsx";
import { Lapsed } from "./screens/Lapsed.tsx";
import { LoadError, Loading } from "./screens/Loading.tsx";
import { Mileage } from "./screens/Mileage.tsx";
import { Report } from "./screens/Report.tsx";
import { SignIn } from "./screens/SignIn.tsx";
import { Trips } from "./screens/Trips.tsx";

const titles: Record<Route["name"], string> = {
  trips: "Trips",
  mileage: "Mileage",
  export: "Mileage Log",
  report: "Mileage Log",
  account: "Account",
  signIn: "Sign In",
  devSession: "Signing In",
};

export function App() {
  const route = useRoute();
  const session = useSession();

  useEffect(() => {
    document.title = `${titles[route.name]} · Carveny Online`;
  }, [route.name]);

  if (route.name === "devSession" && import.meta.env.DEV) return <DevSession />;

  if (session.kind === "loading") {
    return <Shell section={null} lastUpload={undefined}><Loading /></Shell>;
  }
  if (session.kind === "signedOut") return <SignIn />;

  return (
    <AccountProvider userId={session.userId} email={session.email}>
      <ToastProvider>
        <SignedIn route={route} />
      </ToastProvider>
    </AccountProvider>
  );
}

function sectionFor(route: Route): Section {
  switch (route.name) {
    case "mileage": return "mileage";
    case "export": return "export";
    case "account": return "account";
    default: return "trips";
  }
}

function SignedIn({ route }: { route: Route }) {
  const { state, reload } = useAccount();

  useEffect(() => {
    if (route.name === "signIn") navigate("/trips", { replace: true });
  }, [route.name]);

  // The printable report has no app frame; it waits for the data itself.
  if (route.name === "report") return <Report query={route.query} />;

  const section = sectionFor(route);
  if (state.kind === "loading") {
    return <Shell section={section} lastUpload={undefined}><Loading section={section} /></Shell>;
  }
  if (state.kind === "error") {
    return <Shell section={section} lastUpload={undefined}><LoadError message={state.message} retry={reload} /></Shell>;
  }

  const { data } = state;
  if (route.name !== "account" && !data.status.canUpload) {
    // Lapsed or never subscribed: the website is part of the subscription. Restore still works in the app.
    return (
      <Shell section={null} lastUpload={undefined} topEnd={<>
        <span className="cv-sync">{data.email ?? "Signed in with Apple"}</span>
        <button className="cv-btn cv-btn-secondary" type="button" onClick={() => void signOut("signed-out")}>Sign Out</button>
      </>}>
        <Lapsed />
      </Shell>
    );
  }
  let content: React.ReactNode;
  if (route.name === "account") {
    content = <Account />;
  } else if (data.trips.length === 0) {
    content = <Empty retry={reload} />;
  } else if (route.name === "mileage") {
    content = <Mileage />;
  } else if (route.name === "export") {
    content = <Export />;
  } else {
    content = <Trips tripId={route.name === "trips" ? route.tripId : undefined} reviewing={route.name === "trips" && route.review} />;
  }
  return <Shell section={section} lastUpload={data.lastUpload}>{content}</Shell>;
}
